/*
 * The token-first Connect sheet for Expo and Bitrise (platform operators only, SPEC decision 23): the guide to create
 * the right token, what "Verify" answered in people's words, and the account and name Hangar proposes from it. Pure.
 */
import type { ProviderKind } from '@hangar/shared';
import type { ProviderTokenVerifyResult } from '@/api/schemas';

export interface ProviderTokenGuide {
  /** Where the operator creates the token. */
  home: string;
  homeLabel: string;
  /** The kind of token, as the provider calls it. */
  tokenName: string;
  /** Why this kind and not another. */
  why: string;
  steps: string[];
  /** What the picked or typed account is called at the provider. */
  accountLabel: string;
  accountHint: string;
  accountPlaceholder: string;
}

export const PROVIDER_TOKEN_GUIDE: Record<ProviderKind, ProviderTokenGuide> = {
  eas: {
    home: 'https://expo.dev/',
    homeLabel: 'Open expo.dev',
    tokenName: 'Robot token',
    why: "A robot's token keeps working when people leave, and the Developer role is exactly what builds need: publish, view and run.",
    steps: [
      'On expo.dev, sign in as an owner or admin and switch to the organization the builds run on (account switcher, top left).',
      'Open Settings → Access tokens → Robot users → Add robot. Name it hangar and give it the Developer role.',
      'Create a token for the robot, copy it (Expo shows it once) and paste it below. Hangar finds the organization and checks the role.',
    ],
    accountLabel: 'Expo account',
    accountHint: 'The organization name exactly as on expo.dev',
    accountPlaceholder: 'malible-acme',
  },
  bitrise: {
    home: 'https://app.bitrise.io/',
    homeLabel: 'Open bitrise.io',
    tokenName: 'Workspace API token',
    why: "A workspace token belongs to the workspace, not to a person, so it keeps working when people leave. A personal access token works too, but stops with that person's account.",
    steps: [
      'On bitrise.io, open the workspace the builds run on (it needs a plan with build credits).',
      'Open Workspace settings → API tokens → Generate token. Name it hangar and give it a role that may start builds and add apps.',
      'Copy the token (Bitrise shows it once) and paste it below. Hangar lists the workspaces it sees and reads how many builds the plan runs at once.',
    ],
    accountLabel: 'Bitrise workspace',
    accountHint: 'The workspace slug: the id in its bitrise.io URL, not its display name',
    accountPlaceholder: 'a1b2c3d4e5f6a7b8',
  },
};

export type ProviderVerifyTone = 'passed' | 'failed' | 'skipped';

export interface ProviderVerifyRow {
  key: string;
  label: string;
  tone: ProviderVerifyTone;
  message: string;
}

const PROVIDER_NAME: Record<ProviderKind, string> = { eas: 'Expo', bitrise: 'Bitrise' };

function checkLabel(kind: ProviderKind, key: string): string {
  if (key === 'connectivity') return `${PROVIDER_NAME[kind]} answers`;
  if (key === 'credential') return 'Token accepted';
  if (key === 'account') return kind === 'eas' ? 'Can read the Expo account' : 'Can see the workspace';
  if (key === 'permissions') return 'Can build (Developer role)';
  return key;
}

const toneOf = (status: string): ProviderVerifyTone => (status === 'passed' ? 'passed' : status === 'failed' || status === 'error' ? 'failed' : 'skipped');

/** The rows of the verification, in the API's order, with people's labels. */
export const providerVerifyRows = (kind: ProviderKind, r: ProviderTokenVerifyResult): ProviderVerifyRow[] =>
  r.checks.map((c) => ({ key: c.key, label: checkLabel(kind, c.key), tone: toneOf(c.status), message: c.message ?? '' }));

/** True when the token works but the operator still has to pick (or type) the account. */
export const needsAccount = (r: ProviderTokenVerifyResult): boolean =>
  !r.ok && r.checks.every((c) => c.status !== 'failed') && r.checks.some((c) => c.key === 'account' && c.status === 'skipped');

/** The one-line verdict above the rows. */
export function providerVerifyVerdict(kind: ProviderKind, r: ProviderTokenVerifyResult): { tone: ProviderVerifyTone; text: string } {
  const what = kind === 'eas' ? 'Expo account' : 'Bitrise workspace';
  if (r.ok) return { tone: 'passed', text: `The token is valid and can build on ${r.account?.displayName ?? `the ${what}`}.` };
  if (needsAccount(r)) return { tone: 'skipped', text: r.accounts.length ? `The token is valid: pick the ${what} the builds run on.` : `The token is valid: type the ${what} the builds run on.` };
  return { tone: 'failed', text: 'This token cannot be used: fix what failed below, or create a new token.' };
}

/** The values a verification answered for: a change to any of them makes the shown result stale. */
export const providerVerifyKey = (v: { kind: ProviderKind; token: string; externalAccount: string }): string => [v.kind, v.token.trim(), v.externalAccount.trim()].join('\u0000');

/** Ready to ask: a token of plausible length (the API refuses shorter ones). */
export const canVerifyToken = (token: string): boolean => token.trim().length >= 20;

/** The name Hangar proposes once the token is verified: "EAS · <account>" / "Bitrise · <workspace>", like the accounts it creates itself. */
export function proposedProviderName(kind: ProviderKind, r: ProviderTokenVerifyResult | null): string | undefined {
  if (!r?.ok || !r.account) return undefined;
  return `${kind === 'eas' ? 'EAS' : 'Bitrise'} · ${r.account.displayName || r.account.externalAccount}`.slice(0, 120);
}

/** The slots Hangar proposes: what Bitrise says the plan runs at once (1 to 100), else nothing. */
export const proposedSlots = (r: ProviderTokenVerifyResult | null): string | undefined => {
  const c = r?.ok ? r.account?.concurrency : null;
  return typeof c === 'number' && c >= 1 ? String(Math.min(c, 100)) : undefined;
};
